import { useState } from 'react';
import BottomSheet from '../../../../components/ui/BottomSheet';
import { fmtPrice } from '../../../../lib/utils';
import { SideBadge } from '../ManualBadges';

function Field({ label, value }) {
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50 last:border-0">
      <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">{label}</span>
      <span className="text-xs font-mono text-txt-primary">{value}</span>
    </div>
  );
}

// ctx: { strategy, position }. Quantity input is fully manual (no % buttons).
export default function ExitPositionModal({ ctx, onConfirm, onCancel }) {
  const openQty = ctx?.position?.qty ?? 0;
  const integerOnly = Number.isInteger(openQty);
  const [exitQty, setExitQty] = useState(String(openQty)); // default = full open qty

  if (!ctx) return null;
  const { strategy, position } = ctx;

  const qtyNum = Number(exitQty);
  let error = '';
  if (exitQty === '' || Number.isNaN(qtyNum)) error = 'Enter an exit quantity';
  else if (qtyNum <= 0) error = 'Exit quantity must be greater than 0';
  else if (qtyNum > openQty) error = `Exit quantity cannot exceed open qty (${openQty})`;
  else if (integerOnly && !Number.isInteger(qtyNum)) error = 'Exit quantity must be a whole number';
  const valid = !error;

  const body = (
    <>
      <h3 className="font-display font-semibold text-txt-primary mb-4">Exit Position</h3>

      <div className="mb-4">
        <Field label="Strategy" value={strategy.strategyName} />
        <Field label="Symbol" value={position.symbol} />
        <div className="flex items-center justify-between py-1.5 border-b border-bg-border/50">
          <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">Side</span>
          <SideBadge side={position.side} />
        </div>
        <Field label="Open Qty" value={openQty} />
        <Field label="Entry / LTP" value={`${fmtPrice(position.entryPrice)} / ${fmtPrice(position.ltp)}`} />
      </div>

      <label className="block mb-4">
        <span className="text-2xs font-mono uppercase tracking-widest text-txt-label">Exit Qty</span>
        <input
          type="number"
          inputMode="decimal"
          value={exitQty}
          min={integerOnly ? 1 : undefined}
          step={integerOnly ? 1 : 'any'}
          max={openQty}
          onChange={(e) => setExitQty(e.target.value)}
          className="input-field mt-1.5 text-base !h-11 lg:!h-9 lg:text-sm"
          autoFocus
        />
        {error && <span className="block mt-1 text-2xs font-mono text-bear">{error}</span>}
      </label>

      {/* Mobile (<lg): sticky footer within the BottomSheet's scrollable body, matching
          the other confirm sheets' idiom. lg: resets restore the desktop layout. */}
      <div className="sticky bottom-0 -mx-4 px-4 pt-3 pb-2 bg-bg-card border-t border-bg-border flex justify-end gap-3 lg:static lg:mx-0 lg:px-0 lg:pt-0 lg:pb-0 lg:bg-transparent lg:border-0">
        <button type="button" onClick={onCancel} className="btn-ghost min-h-[48px] lg:min-h-0">Cancel</button>
        <button
          type="button"
          disabled={!valid}
          onClick={() => onConfirm({ strategy, position, exitQty: qtyNum })}
          className="btn-danger disabled:opacity-40 disabled:cursor-not-allowed min-h-[48px] lg:min-h-0"
        >
          Confirm Exit
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop (>=lg): unchanged centered modal */}
      <div className="hidden lg:flex fixed inset-0 z-50 items-center justify-center bg-black/60 backdrop-blur-sm p-4">
        <div className="card p-6 w-full max-w-sm animate-fade-in shadow-panel">
          {body}
        </div>
      </div>

      {/* Mobile (<lg): bottom-sheet presentation of the same content. */}
      <BottomSheet open onClose={onCancel} title="">
        {body}
      </BottomSheet>
    </>
  );
}
